import { useOverlayPosition } from '@react-aria/overlays'
import { RefObject, useLayoutEffect, useMemo, useState } from 'react'

export const useOverlayHack = (
  overlayRef: RefObject<HTMLElement>,
  anchorRef: RefObject<HTMLElement>,
  isOverlayOpen: boolean,
  buttonHeight: number,
  buttonOffset: number,
  dependency?: any,
) => {
  const [shouldFlipOverlayPlacement, setShouldFlipOverlayPlacement] = useState(false)

  // HACK: trying to figure out if the overlay should be flipped ourselves since react-aria is buggy
  useLayoutEffect(() => {
    // we use a timeout here to wait until overlay is rendered
    setTimeout(() => {
      if (!overlayRef.current) return
      const overlayIsTooTall = overlayRef.current.scrollHeight > overlayRef.current.offsetHeight
      /* the user is going to open an overflowing overlay, and we are going to flip it, causing it to no longer
      overflow, which will cause us to flip it back, and getting stuck in a loop. So we don't flip it when that
      happens */
      if (shouldFlipOverlayPlacement !== overlayIsTooTall) {
        setShouldFlipOverlayPlacement(overlayIsTooTall || isOverlayOpen)
      }
    }, 0)
  }, [isOverlayOpen, shouldFlipOverlayPlacement, overlayRef, dependency])

  const { overlayProps: positionProps } = useOverlayPosition({
    targetRef: anchorRef,
    overlayRef,
    placement: shouldFlipOverlayPlacement ? 'top left' : 'bottom right',
    offset: -buttonHeight - buttonOffset,
    isOpen: true,
    boundaryElement: document.getElementById('workspace-dashboard') || document.body,
    shouldFlip: false,
  })

  // @react-aria/overlays has a bug where it doesn't pass the correct bottom value to the overlay
  const fixedBottom = useMemo(() => {
    // typescript tells us that this might be a string (I've never seen it but ¯\_(ツ)_/¯)
    const bottom =
      typeof positionProps.style?.bottom === 'string'
        ? parseFloat(positionProps.style?.bottom)
        : positionProps.style?.bottom
    // if the bottom value is undefined or a number, that means it's not broken
    if (bottom === undefined || !isNaN(bottom)) return
    // if we're here, bottom is guaranteed to be NaN
    // try to determine the correct bottom value by calculating it off of the buttonRef rect and the window
    return window.innerHeight - (anchorRef.current?.getBoundingClientRect().bottom ?? 0)
  }, [positionProps.style?.bottom, anchorRef])

  return {
    shouldFlipOverlayPlacement,
    positionPropsStyle: { ...positionProps.style, bottom: fixedBottom },
  }
}
